import { useState, useEffect, useCallback } from 'react';
import { where } from 'firebase/firestore';
import { firestoreWrapper } from '@/services/firestoreWrapper';
import { getAllUserAppAccess, updateUserAppAccess, UserAppAccess } from '@/services/appAccess';

// Cache configuration
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const ADMIN_APP_ACCESS_CACHE_KEY = 'adminAppAccessCache';

interface CacheEntry {
  data: UserAppAccess[];
  timestamp: number;
}

// In-memory cache
let memoryCache: CacheEntry | null = null;

export const useAdminAppAccess = () => {
  const [userAppAccess, setUserAppAccess] = useState<UserAppAccess[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserAppAccess = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Check memory cache first
      const now = Date.now();
      if (memoryCache && now - memoryCache.timestamp < CACHE_TTL) {
        console.log('Using cached admin app access');
        setUserAppAccess(memoryCache.data);
        setIsLoading(false);
        return;
      }

      // Check localStorage cache
      const cachedData = localStorage.getItem(ADMIN_APP_ACCESS_CACHE_KEY);
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData) as CacheEntry;
          if (now - parsed.timestamp < CACHE_TTL) {
            console.log('Using localStorage cached admin app access');
            memoryCache = parsed;
            setUserAppAccess(parsed.data);
            setIsLoading(false);
            return;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }

      console.log('Fetching fresh admin app access');
      
      // Get all allowed emails first
      const emailsRef = firestoreWrapper.collection('allowedEmails');
      const emailsSnapshot = await firestoreWrapper.getDocs(emailsRef);
      const allowedEmails = emailsSnapshot.docs
        .filter(doc => doc.data() && doc.data().email) // Filter out docs with missing email
        .map(doc => doc.data().email.toLowerCase());

      // Then get their app access
      const access = await getAllUserAppAccess();
      
      // Make sure we have an entry for every allowed email
      const accessMap = new Map(access
        .filter(a => a && a.email) // Filter out entries with missing email
        .map(a => [a.email.toLowerCase(), a.allowedApps]));
      const allAccess = allowedEmails.map(email => ({
        email,
        allowedApps: accessMap.get(email) || []
      }));

      // Cache the result
      const cacheEntry: CacheEntry = {
        data: allAccess,
        timestamp: now
      };
      memoryCache = cacheEntry;
      localStorage.setItem(ADMIN_APP_ACCESS_CACHE_KEY, JSON.stringify(cacheEntry));
      
      setUserAppAccess(allAccess);
    } catch (error) {
      console.error('Error fetching user app access:', error);
      setError('Failed to fetch user app access');
      setUserAppAccess([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateBulkUserAppAccess = async (emails: string[], appId: string, shouldAdd: boolean) => {
    setError(null);
    try {
      const updates = emails.map(async (email) => {
        const currentAccess = userAppAccess.find(access => 
          access.email.toLowerCase() === email.toLowerCase()
        );
        
        let newAllowedApps: string[];
        if (currentAccess) {
          newAllowedApps = shouldAdd
            ? [...new Set([...currentAccess.allowedApps, appId])]
            : currentAccess.allowedApps.filter(id => id !== appId);
        } else {
          newAllowedApps = shouldAdd ? [appId] : [];
        }

        await updateUserAppAccess(email, newAllowedApps);
      });

      await Promise.all(updates);
      
      // Clear cache
      clearCache();
      
      await fetchUserAppAccess(); // Refresh the data
      return true;
    } catch (err) {
      console.error('Error updating bulk app access:', err);
      setError(err instanceof Error ? err.message : 'Failed to update app access');
      return false;
    }
  };

  useEffect(() => {
    fetchUserAppAccess();
  }, [fetchUserAppAccess]);

  return {
    userAppAccess,
    isLoading,
    error,
    fetchUserAppAccess,
    updateUserAppAccess,
    updateBulkUserAppAccess
  };
};

// Clear cache
export const clearCache = () => {
  memoryCache = null;
  localStorage.removeItem(ADMIN_APP_ACCESS_CACHE_KEY);
};
